import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  BarChart3, 
  Zap, 
  Shield, 
  Globe, 
  TrendingUp,
  Target,
  DollarSign,
  Users,
  Layers
} from "lucide-react";
import { Button } from "../components/ui/button";

const LandingPage = () => {
  const features = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Precision Targeting",
      description: "Reach your audience with geo, demographic, and behavioral targeting powered by AI."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Real-Time Bidding",
      description: "Lightning-fast RTB engine processing millions of ad requests per second."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Advanced Analytics",
      description: "Deep insights into CTR, ROAS, and campaign performance in real-time."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Fraud Protection",
      description: "Enterprise-grade fraud detection and brand safety controls."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Reach",
      description: "Access premium inventory across thousands of publishers worldwide."
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Maximize ROI",
      description: "AI-powered budget optimization to get the most from every dollar."
    }
  ];

  const stats = [
    { value: "10B+", label: "Daily Impressions" },
    { value: "5,000+", label: "Publishers" },
    { value: "99.9%", label: "Uptime" },
    { value: "<50ms", label: "Response Time" }
  ];

  return (
    <div className="min-h-screen bg-[#09090B] relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white">MediaTNT</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-muted-foreground hover:text-white transition-colors">Features</a>
              <a href="#publishers" className="text-muted-foreground hover:text-white transition-colors">Publishers</a>
              <a href="#advertisers" className="text-muted-foreground hover:text-white transition-colors">Advertisers</a>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to="/auth">
                <Button variant="ghost" className="text-muted-foreground hover:text-white" data-testid="login-btn">
                  Log In
                </Button>
              </Link>
              <Link to="/auth?mode=register">
                <Button className="bg-primary hover:bg-indigo-hover btn-glow" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-night-paper border border-night-subtle mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald animate-pulse" />
              <span className="text-sm text-muted-foreground">Platform Status: Operational</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
              The Ad Platform Built for 
              <span className="text-primary"> Performance</span>
            </h1>
            
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Connect advertisers with premium publishers through our AI-powered ad exchange. 
              Real-time bidding, precision targeting, and transparent analytics.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth?mode=register&role=advertiser">
                <Button size="lg" className="bg-primary hover:bg-indigo-hover btn-glow px-8" data-testid="start-advertising-btn">
                  Start Advertising
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/auth?mode=register&role=publisher">
                <Button size="lg" variant="outline" className="border-night-subtle hover:bg-night-paper px-8" data-testid="monetize-traffic-btn">
                  Monetize Your Traffic
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-lg bg-night-paper border border-night-subtle card-hover"
                data-testid={`stat-${index}`}
              >
                <div className="font-heading text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 md:px-8 lg:px-12 bg-night-paper/50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Enterprise-Grade Ad Technology
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Everything you need to run successful advertising campaigns at scale.
            </p>
          </motion.div>

          <div className="bento-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="p-6 rounded-lg bg-night-paper border border-night-subtle card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`feature-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Publishers Section */}
      <section id="publishers" className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/10 text-emerald text-sm mb-4">
                <Users className="w-4 h-4" />
                For Publishers
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                Maximize Your Revenue
              </h2>
              <p className="text-muted-foreground mb-6">
                Access premium demand from thousands of advertisers. Set floor prices, 
                control ad placements, and watch your eCPM soar.
              </p>
              <ul className="space-y-3 mb-8">
                {["Header bidding integration", "Real-time revenue reporting", "Yield optimization tools", "Brand safety controls"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-muted-foreground">
                    <div className="w-5 h-5 rounded-full bg-emerald/20 flex items-center justify-center">
                      <TrendingUp className="w-3 h-3 text-emerald" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/auth?mode=register&role=publisher">
                <Button className="bg-emerald hover:bg-emerald/90" data-testid="join-publisher-btn">
                  Join as Publisher
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-lg bg-night-paper border border-night-subtle p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Today's Revenue</span>
                  <span className="text-emerald text-sm">+12.5%</span>
                </div>
                <div className="font-heading text-4xl font-bold text-white">$2,847.50</div>
                <div className="h-32 flex items-end gap-1">
                  {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((h, i) => (
                    <div 
                      key={i} 
                      className="flex-1 bg-emerald/20 rounded-t"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-night-subtle">
                  <div>
                    <div className="text-sm text-muted-foreground">Impressions</div>
                    <div className="font-semibold text-white">1.2M</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Fill Rate</div>
                    <div className="font-semibold text-white">94.5%</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">eCPM</div>
                    <div className="font-semibold text-white">$2.37</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advertisers Section */}
      <section id="advertisers" className="py-20 px-4 md:px-8 lg:px-12 bg-night-paper/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="order-2 lg:order-1 relative"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-lg bg-night-paper border border-night-subtle p-6 space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-semibold text-white">Campaign Performance</span>
                  <span className="text-xs px-2 py-1 rounded bg-primary/20 text-primary">Live</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded bg-night-bg border border-night-subtle">
                    <div className="text-sm text-muted-foreground mb-1">CTR</div>
                    <div className="font-heading text-2xl font-bold text-white">2.8%</div>
                    <div className="text-xs text-emerald">+0.3% vs avg</div>
                  </div>
                  <div className="p-4 rounded bg-night-bg border border-night-subtle">
                    <div className="text-sm text-muted-foreground mb-1">ROAS</div>
                    <div className="font-heading text-2xl font-bold text-white">4.2x</div>
                    <div className="text-xs text-emerald">+18% this week</div>
                  </div>
                  <div className="p-4 rounded bg-night-bg border border-night-subtle">
                    <div className="text-sm text-muted-foreground mb-1">Spend</div>
                    <div className="font-heading text-2xl font-bold text-white">$5,420</div>
                    <div className="text-xs text-muted-foreground">of $10,000</div>
                  </div>
                  <div className="p-4 rounded bg-night-bg border border-night-subtle">
                    <div className="text-sm text-muted-foreground mb-1">Conversions</div>
                    <div className="font-heading text-2xl font-bold text-white">847</div>
                    <div className="text-xs text-emerald">$6.40 CPA</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm mb-4">
                <Target className="w-4 h-4" />
                For Advertisers
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                Drive Real Results
              </h2>
              <p className="text-muted-foreground mb-6">
                Launch campaigns in minutes with our self-serve platform. 
                AI-powered optimization ensures every dollar works harder.
              </p>
              <ul className="space-y-3 mb-8">
                {["Drag-and-drop campaign builder", "AI budget optimization", "Real-time performance dashboards", "Advanced retargeting"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-muted-foreground">
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <Zap className="w-3 h-3 text-primary" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/auth?mode=register&role=advertiser">
                <Button className="bg-primary hover:bg-indigo-hover btn-glow" data-testid="start-campaign-btn">
                  Start Your Campaign
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center p-12 rounded-2xl bg-gradient-to-br from-primary/20 to-transparent border border-primary/30"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Advertising?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Join thousands of advertisers and publishers using MediaTNT to drive growth.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth?mode=register">
                <Button size="lg" className="bg-primary hover:bg-indigo-hover btn-glow px-8" data-testid="cta-get-started-btn">
                  Get Started Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-night-subtle hover:bg-night-paper px-8">
                Schedule Demo
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 lg:px-12 border-t border-night-subtle">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white">MediaTNT</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2024 MediaTNT. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
